import SEO_ROUTES from "@/data/seoRoutes.json";
import { Link } from "react-router-dom";
import { ArrowRight, PackageSearch, Package, Truck, Users, Gem, Scale, Leaf, User } from "lucide-react";
import { Seo, breadcrumbSchema, organizationSchema } from "@/components/site/Seo";
import { PageHero, LifestyleImage } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { WhatsAppButton } from "@/components/site/WhatsAppButton";
import { PRODUCTS } from "@/data/products";
import { BUSINESS, IMAGES, SITE_URL } from "@/config/site";

const crumbs = [
  { name: "Home", path: "/" },
  { name: "About Us", path: "/about" },
];

const WHAT_WE_DO = [
  { icon: PackageSearch, t: "Sourcing", b: "We source the natural wellness products in our range." },
  { icon: Package, t: "Packaging", b: "We package products for our customers." },
  { icon: Users, t: "Supplying", b: "We supply customers directly, starting with a conversation." },
  { icon: Truck, t: "Distributing", b: "We distribute from Gugulethu, Cape Town." },
];

const VALUES = [
  { icon: Gem, t: "Quality", b: "We take care in every step, from sourcing to the moment a product reaches you." },
  { icon: Scale, t: "Integrity", b: "We are honest about our products. If a detail is not yet verified, we say so." },
  { icon: Leaf, t: "Wellness", b: "We are passionate about healthier lifestyles and nature-inspired everyday products." },
];

export default function About() {
  return (
    <>
      <Seo
        title={SEO_ROUTES["/about"].title}
        description={SEO_ROUTES["/about"].description}
        path="/about"
        image={`https://images.unsplash.com/${IMAGES.coastline.id}?auto=format&fit=crop&w=1200&q=70`}
        jsonLd={[
          breadcrumbSchema(crumbs),
          organizationSchema(),
          { "@context": "https://schema.org", "@type": "AboutPage", name: "About Uhlumo Organics", url: `${SITE_URL}/about`, about: { "@id": `${SITE_URL}/#organization` } },
        ]}
      />
      <PageHero
        crumbs={crumbs}
        eyebrow={`Established ${BUSINESS.established} · Gugulethu, Cape Town`}
        title="About Uhlumo Organics"
        intro="Uhlumo Organics was established in 2024 with a passion for natural health and wellness. We source, package, supply and distribute natural wellness products."
        aside={<LifestyleImage id={IMAGES.coastline.id} alt={IMAGES.coastline.alt} priority className="aspect-[4/3]" />}
      />

      <section className="section" aria-labelledby="story-heading">
        <div className="container-site grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow">Our story</p>
            <h2 id="story-heading" className="h-section mt-3">
              Making natural wellness more accessible
            </h2>
          </Reveal>
          <Reveal className="space-y-4 text-[15px] leading-relaxed text-muted-foreground sm:text-base lg:col-span-7">
            <p>Uhlumo Organics is a South African natural wellness brand founded with a passion for healthier lifestyles and nature-inspired products.</p>
            <p>
              Our mission is to make natural wellness products more accessible, while providing customers with useful information about our product range. We want to build a trusted brand associated with quality ingredients, customer care and everyday wellness.
            </p>
            <p>
              Our product range includes{" "}
              {PRODUCTS.map((p, i) => (
                <span key={p.slug}>
                  <Link to={p.path} className="link-accent">
                    {p.fullName || p.name}
                  </Link>
                  {i < PRODUCTS.length - 2 ? ", " : i === PRODUCTS.length - 2 ? " and " : "."}
                </span>
              ))}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="section surface-sage" aria-labelledby="do-heading">
        <div className="container-site">
          <h2 id="do-heading" className="h-section">
            What we do
          </h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHAT_WE_DO.map(({ icon: Icon, t, b }, i) => (
              <Reveal key={t} delay={i * 60} className="card-soft p-6">
                <Icon className="h-6 w-6 text-[color:var(--u-green)]" aria-hidden="true" />
                <h3 className="mt-4 text-xl font-semibold">{t}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{b}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section" aria-labelledby="values-heading">
        <div className="container-site">
          <div className="mx-auto max-w-2xl text-center">
            <p className="eyebrow justify-center">Our values</p>
            <h2 id="values-heading" className="h-section mt-3">
              Quality. Integrity. Wellness.
            </h2>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {VALUES.map(({ icon: Icon, t, b }, i) => (
              <Reveal key={t} delay={i * 80} className="card-soft p-7 text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#EEF3EC] text-[color:var(--u-green)]">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="mt-4 text-2xl font-semibold">{t}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">{b}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section surface-sand" aria-labelledby="founder-heading" data-testid="about-founder">
        <div className="container-site grid gap-10 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-4">
            <div className="placeholder-product aspect-[4/5]" role="img" aria-label="Founder photograph coming soon">
              <User className="h-10 w-10 text-[color:var(--u-brown-dark)]" aria-hidden="true" />
              <span className="mt-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Founder photograph coming soon</span>
            </div>
          </div>
          <div className="lg:col-span-8">
            <p className="eyebrow">Meet the founder</p>
            <h2 id="founder-heading" className="h-section mt-3">
              {BUSINESS.founder}
            </h2>
            <p className="mt-2 text-sm font-semibold text-[color:var(--u-brown-dark)]">Founder, Uhlumo Organics</p>
            <p className="mt-5 text-[15px] leading-relaxed text-muted-foreground sm:text-base">
              {BUSINESS.founderShort} founded Uhlumo Organics in {BUSINESS.established} in Gugulethu, Cape Town, with a passion for natural health and wellness and a goal of making natural wellness products more accessible to South African customers.
            </p>
            <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground sm:text-base">
              Under her leadership, the business focuses on building customer trust through clear product information and personal service.
            </p>
          </div>
        </div>
      </section>

      <section className="section" aria-labelledby="trust-heading">
        <div className="container-site grid gap-10 lg:grid-cols-2">
          <div>
            <h2 id="trust-heading" className="h-section">
              How we build trust
            </h2>
            <ul className="mt-6 space-y-4 text-[15px] leading-relaxed text-muted-foreground">
              <li>
                <strong className="text-foreground">Clear product information.</strong> We describe what each product is and publish label details only once they are verified.
              </li>
              <li>
                <strong className="text-foreground">No exaggerated claims.</strong> We do not claim that our products diagnose, treat, cure or prevent any disease.
              </li>
              <li>
                <strong className="text-foreground">Personal service.</strong> Every order starts with a conversation so that you can ask questions before you buy.
              </li>
            </ul>
          </div>
          <div className="card-soft flex flex-col justify-center p-7 sm:p-9">
            <h3 className="text-2xl font-semibold">Have a question about our products?</h3>
            <p className="mt-3 text-[15px] text-muted-foreground">Talk to us on WhatsApp or send an enquiry.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/products" className="btn btn-primary" data-testid="about-explore-products">
                Explore Products <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <WhatsAppButton testid="about-whatsapp" location="about" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
